import { useState } from 'react';

import Modal from './Modal.jsx';
import { Button } from './ui.jsx';
import { api } from '../lib/api.js';
import { currency, rate, tenure } from '../lib/format.js';

/**
 * ProceedModal — the confirmation step after a plan is chosen.
 *
 * This is where the selection is committed to the backend (POST .../proceed),
 * which records a lead capturing exactly which variant and tenure was picked.
 *
 * The modal has three states — review, submitting, done — held locally rather
 * than lifted, since nothing outside the dialog needs them. On success the copy
 * is explicit that the next real-world step is pledging units, so the demo never
 * implies a payment has been taken.
 */

function SummaryRow({ label, value, emphasis = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="text-sm text-ink-600">{label}</dt>
      <dd className={emphasis ? 'text-base font-bold text-ink-950' : 'text-sm font-semibold text-ink-900'}>
        {value}
      </dd>
    </div>
  );
}

export default function ProceedModal({ open, onClose, product, variant, plan }) {
  const [status, setStatus] = useState('review'); // review | submitting | done | error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const close = () => {
    onClose();
    // Reset after the closing transition so the content does not visibly snap
    // back to the review state while the dialog is still fading out.
    setTimeout(() => {
      setStatus('review');
      setResult(null);
      setErrorMessage(null);
    }, 200);
  };

  const confirm = async () => {
    setStatus('submitting');
    setErrorMessage(null);
    try {
      const response = await api.recordProceed(product.slug, {
        variantId: variant.id,
        tenureMonths: plan.tenureMonths,
        monthlyAmount: plan.monthlyAmount,
      });
      setResult(response);
      setStatus('done');
    } catch (err) {
      setErrorMessage(err.message);
      setStatus('error');
    }
  };

  if (!product || !variant || !plan) return null;

  const isDone = status === 'done';

  return (
    <Modal
      open={open}
      onClose={close}
      title={isDone ? 'Plan reserved' : 'Confirm your EMI plan'}
      description={
        isDone
          ? undefined
          : 'Review the numbers below. Nothing is charged at this step — the next stage is pledging your units.'
      }
      footer={
        isDone ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close} disabled={status === 'submitting'}>
              Back
            </Button>
            <Button onClick={confirm} loading={status === 'submitting'}>
              {status === 'submitting' ? 'Reserving…' : 'Confirm plan'}
            </Button>
          </>
        )
      }
    >
      {isDone ? (
        <div className="text-center">
          <span
            className="mx-auto grid size-14 place-items-center rounded-full bg-growth-100 text-growth-700"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" className="size-7" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>

          <p className="mt-4 text-sm leading-relaxed text-ink-700">{result?.message}</p>

          {result?.applicationId && (
            <p className="mt-4 inline-block rounded-lg bg-ink-100 px-3 py-2 font-mono text-xs text-ink-700">
              Reference: {result.applicationId}
            </p>
          )}

          <p className="mt-5 text-xs leading-relaxed text-ink-500">
            In a live deployment this is where you would be handed off to the lender&rsquo;s pledge flow to
            lien-mark your mutual fund units and complete KYC.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-4">
            <img
              src={variant.images?.[0] || product.heroImage}
              alt=""
              className="size-16 shrink-0 rounded-xl border border-ink-200 object-cover"
            />
            <div className="min-w-0">
              <p className="font-semibold text-ink-950">{product.name}</p>
              <p className="mt-0.5 text-sm text-ink-600">{variant.label}</p>
            </div>
          </div>

          <dl className="mt-5 divide-y divide-ink-100 border-y border-ink-200">
            <SummaryRow label="Monthly instalment" value={`${currency(plan.monthlyAmount)}/mo`} emphasis />
            <SummaryRow label="Tenure" value={tenure(plan.tenureMonths)} />
            <SummaryRow
              label="Interest rate"
              value={plan.isNoCostEmi ? 'No cost EMI (0%)' : `${rate(plan.interestRate)} p.a.`}
            />
            {plan.cashback > 0 && <SummaryRow label="Cashback" value={currency(plan.cashback)} />}
            <SummaryRow label="Total payable" value={currency(plan.totalPayable)} />
            <SummaryRow label="Units to pledge" value={currency(plan.collateral.pledgeRequired)} />
          </dl>

          {errorMessage && (
            <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
